import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";

import { COLOR_THEME_SUPPORTED_DOMAINS } from "../constants/color-theme.constant";
import { DomainService } from "./domain.service";

export interface ThemeColorOption {
  key: string;
  label: string;
  color: string;
  primary: string;
  secondary: string;
  tertiary: string;
}

@Injectable({
  providedIn: "root",
})
export class ThemeColorService {
  private readonly storageKey = "app-theme-color";

  readonly supportsColorTheme: boolean;

  readonly colorOptions: ThemeColorOption[] = [
    { key: "navy-blue", label: "Navy Blue", color: "#020d31", primary: "#f5b430", secondary: "#020d31", tertiary: "#5d6f7a" },
    { key: "modern-olive", label: "Modern Olive", color: "#1c280d", primary: "#f5b430", secondary: "#1c280d", tertiary: "#5d6f7a" },
    { key: "dark-gray", label: "Dark Gray", color: "#12191a", primary: "#f5b430", secondary: "#12191a", tertiary: "#5d6f7a" },
    { key: "warm-ember", label: "Warm Ember", color: "#230501", primary: "#f5b430", secondary: "#230501", tertiary: "#5d6f7a" },
    { key: "dark-green", label: "Dark Green", color: "#1B4332", primary: "#5AC481FF", secondary: "#1B4332", tertiary: "#ffffff" },
    { key: "silver-grey", label: "Silver Grey", color: "#5e6d78", primary: "#e7eff5", secondary: "#5e6d78", tertiary: "#F5F6F7" },
    { key: "dark-sky-blue", label: "Dark Sky Blue", color: "#19639A", primary: "#f5b430", secondary: "#19639A", tertiary: "#F4F7FC" },
    { key: "chocolate-brown", label: "Chocolate Brown", color: "#3f2515", primary: "#E8D5B7", secondary: "#3f2515", tertiary: "#FAF6F0" },
    { key: "charcoal-grey", label: "Charcoal Grey", color: "#2E3440", primary: "#9AA3B0", secondary: "#2E3440", tertiary: "#F5F6F7" },
    { key: "deep-space", label: "Deep Space", color: "#16162A", primary: "#E8A050", secondary: "#16162A", tertiary: "#FFF8F0" },
    { key: "deep-rose", label: "Deep Rose", color: "#c4606e", primary: "#ecdfe1", secondary: "#c4606e", tertiary: "#e4c8cc" },
    { key: "light-gold", label: "Light Gold", color: "#e0ae01", primary: "#f6efdb", secondary: "#e0ae01", tertiary: "#FFBF0" },
  ];

  readonly defaultColor = "navy-blue";

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly domainService: DomainService,
  ) {
    this.supportsColorTheme = (COLOR_THEME_SUPPORTED_DOMAINS as readonly string[]).includes(this.domainService.getDomain());
    // only apply if user has explicitly picked a theme. Otherwise leave WL SCSS untouched.
    if (this.supportsColorTheme && localStorage.getItem(this.storageKey)) {
      this.applyThemeColor(this.getThemeColor());
    }
  }

  getThemeColor(): string {
    const color = localStorage.getItem(this.storageKey);
    return this.colorOptions.some((opt) => opt.key === color) ? color! : this.defaultColor;
  }

  hasStoredTheme(): boolean {
    return localStorage.getItem(this.storageKey) !== null;
  }

  setThemeColor(colorKey: string): void {
    const option = this.colorOptions.find((opt) => opt.key === colorKey);
    if (!option) {
      console.warn(`Invalid theme color: "${colorKey}". Falling back to default.`);
      colorKey = this.defaultColor;
    }

    localStorage.setItem(this.storageKey, colorKey);
    this.applyThemeColor(colorKey);
  }

  resetThemeColor(): void {
    localStorage.removeItem(this.storageKey);
    const existingStyle = this.document.getElementById("custom-theme-color");
    if (existingStyle) existingStyle.remove();
  }

  private applyThemeColor(colorKey: string): void {
    const option = this.colorOptions.find((opt) => opt.key === colorKey);
    if (!option) return;

    const existingStyle = this.document.getElementById("custom-theme-color");
    if (existingStyle) existingStyle.remove();

    const style = this.document.createElement("style");
    style.id = "custom-theme-color";

    const primary = option.primary;
    const secondary = option.secondary;
    const tertiary = option.tertiary;
    const primaryContrast = this.getContrastColor(primary);
    const secondaryContrast = this.getContrastColor(secondary);
    const secondaryShade = this.adjustColor(secondary, -10);
    const secondaryTint = this.adjustColor(secondary, 10);

    // (mirrors the conventions in assets/scss/themecolors/_freedemokit.scss)
    // - secondary = dark chrome (headers, tabs, nav)
    // - primary   = accent/action (user button, active tab, dividers)
    const ionPrimary = this.generateIonVars("primary", primary);
    const ionSecondary = this.generateIonVars("secondary", secondary);
    const ionTertiary = this.generateIonVars("tertiary", tertiary);
    const ionHeader = this.generateIonVars("header", secondary);
    const ionDrawer = this.generateIonVars("drawer", secondary);

    style.textContent = `
      * {
        /* semantic tokens (override brand SCSS with !important) */
        --bg-primary: ${secondary} !important;
        --bg-secondary: ${primary} !important;
        --header-bg: ${secondary} !important;
        --subheader-bg: ${secondaryShade} !important;
        --sport-nav-bg: ${secondary} !important;
        --sport-event-bg: ${secondary} !important;
        --tab-bg: ${secondary} !important;
        --sport-tab-bg: ${secondary} !important;
        --table-header-bg: ${secondary} !important;
        --tab-active-bg: ${primary} !important;
        --sport-tab-active-bg: ${primary} !important;
        --user-button-bg: ${primary} !important;
        --divider-bg: ${primary} !important;

        --text-primary: ${secondary} !important;
        --text-secondary: ${primary} !important;
        --primary-contrast: ${primaryContrast} !important;
        --secondary-contrast: ${secondaryContrast} !important;
        --header-text: ${secondaryContrast} !important;
        --subheader-text: ${secondaryContrast} !important;
        --tab-text: ${secondaryContrast} !important;
        --tab-active-text: ${primaryContrast} !important;
        --sport-nav-text: ${secondaryContrast} !important;
        --sport-tab-text: ${secondaryContrast} !important;
        --sport-tab-active-text: ${primaryContrast} !important;
        --user-button-text: ${primaryContrast} !important;

        /* Brand hex aliases (some components reference these directly) */
        --color-navy: ${secondary} !important;
        --color-navy-tint: ${secondaryTint} !important;
        --color-midnight-navy: ${secondaryShade} !important;
        --color-yellow: ${primary} !important;

        /* Ionic-style vars */
        ${ionPrimary}
        ${ionSecondary}
        ${ionTertiary}
        ${ionHeader}
        ${ionDrawer}
      }
    `;
    this.document.head.appendChild(style);
  }

  private generateIonVars(name: string, hex: string): string {
    const rgb = this.hexToRgb(hex);
    const shade = this.adjustColor(hex, -10);
    const tint = this.adjustColor(hex, 10);
    const contrast = this.getContrastColor(hex);
    const contrastRgb = this.hexToRgb(contrast);

    return `
      --ion-color-${name}: ${hex} !important;
      --ion-color-${name}-rgb: ${rgb} !important;
      --ion-color-${name}-contrast: ${contrast} !important;
      --ion-color-${name}-contrast-rgb: ${contrastRgb} !important;
      --ion-color-${name}-shade: ${shade} !important;
      --ion-color-${name}-tint: ${tint} !important;
    `;
  }

  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : "0, 0, 0";
  }

  private adjustColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255))
        .toString(16)
        .slice(1)
    );
  }

  private getContrastColor(hex: string): string {
    const rgbStr = this.hexToRgb(hex);
    const [r, g, b] = rgbStr.split(",").map((x) => parseInt(x.trim()));
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? "#000000" : "#ffffff";
  }
}
